import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import { ClientAmountDueBox } from '@/components/ClientAmountDueBox';
import { DepositSelector } from '@/components/DepositSelector';
import { useNavigate } from '@/components/Link/Link';
import { RequestOrganizationPrice } from '@/components/RequestOrganizationPrice';
import { RequestWorkshopPrice } from '@/components/RequestWorkshopPrice';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { useBrands } from '@/models/brand';
import { useClientPurchaseHistory } from '@/models/client';
import { useCreateDraftRequestArticle, useIsUpdatingClientExternalId } from '@/models/request';
import {
  CreationArticlesCardList,
  CreationArticlesTable,
} from '@/routes/Requests/components/ArticlesTable';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

import { AddItemFromPurchaseHistoryModal } from './AddItemFromPurchaseHistoryModal';
import { ApplyHandlingFee } from './ApplyHandlingFee';
import { BrandModal } from './BrandModal';
import { ServiceEligibilityModal } from './ServiceEligibilityModal';

export const NewRequestArticles = () => {
  const { isMobile, isTablet, isWidescreen } = useViewPort();
  const {
    request,
    view: {
      workshopPrice,
      organizationPrice,
      items: { serviceEligibility },
    },
    errors,
    workflow,
  } = useRequestContext();

  const { data: brandsData } = useBrands({
    organizationId: request.organizationId,
    limit: 2,
    offset: 0,
  });

  const isOrganizationMultiBrands = !!brandsData?.brands && brandsData.brands.length > 1;

  const hasArticles = request.articles.length > 0;
  const navigate = useNavigate();
  const [isPurchaseHistoryModalOpen, setIsPurchaseHistoryModalOpen] = useState(false);
  const [isServiceEligibilityModalOpen, setIsServiceEligibilityModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);

  const {
    mutateAsync: createArticle,
    isPending: isPendingCreateArticle,
    isSuccess: isSuccessCreateArticle,
    reset: resetCreateArticle,
  } = useCreateDraftRequestArticle(request.id);

  const isClientManagerEnabled = !!workflow?.config.clientManagement?.enabled;

  const isUpdatingExternalId = useIsUpdatingClientExternalId(request.id);

  const { data: clientPurchaseHistory, isLoading: isLoadingClientPurchaseHistory } =
    useClientPurchaseHistory({
      clientId: request.client?.id,
      clientExternalId: request.client?.externalId,
      enabled: !!isClientManagerEnabled && !isUpdatingExternalId,
    });

  const handleCreateArticle = async ({
    productId,
    purchaseDate,
    serialNumber,
  }: {
    productId?: string;
    purchaseDate?: string;
    serialNumber?: string;
  } = {}) => {
    const { id } = await createArticle(
      productId
        ? {
            productId,
            purchaseDate,
            serialNumber,
            soldThroughNetwork: true,
            dataFromPurchaseHistory: {
              productId,
              serialNumber,
              purchaseDate,
            },
          }
        : undefined
    );

    navigate(`/requests/new/${request.id}/articles/${id}`, {
      state: { from: 'request', isNew: true, skipRouterBlocker: true },
      replace: true,
    });
    resetCreateArticle();
  };

  const handleAddItemClick = () => {
    if (clientPurchaseHistory && clientPurchaseHistory.purchases?.length > 0) {
      setIsPurchaseHistoryModalOpen(true);
    } else if (serviceEligibility.enabled) {
      setIsServiceEligibilityModalOpen(true);
    } else if (isOrganizationMultiBrands) {
      setIsBrandModalOpen(true);
    } else {
      handleCreateArticle();
    }
  };

  const handleAddNewItem = async () => {
    if (serviceEligibility.enabled) {
      setIsServiceEligibilityModalOpen(true);
    } else if (isOrganizationMultiBrands) {
      setIsBrandModalOpen(true);
    } else {
      await handleCreateArticle();
    }
  };

  const hasReachedMaxArticles =
    !!workflow?.config?.fields?.request?.articles?.max &&
    request.articles.length >= workflow.config.fields.request.articles.max;
  const showAddArticleButton = request.draft && !hasReachedMaxArticles;

  return (
    <Stack
      gap="1rem"
      style={{ border: '1px solid var(--color-neutral-300)', borderRadius: '8px', padding: '1rem' }}
    >
      <Box padding={isMobile || isTablet ? undefined : '0'}>
        {hasArticles &&
          (isMobile || isTablet ? <CreationArticlesCardList /> : <CreationArticlesTable />)}

        {(!hasArticles || showAddArticleButton || errors.articles?.hasError) && (
          <Stack
            padding={isMobile || isTablet ? undefined : '1rem'}
            alignItems={isMobile || isTablet ? 'stretch' : 'flex-start'}
            gap={isMobile || isTablet ? '0.5rem' : '1rem'}
            style={
              isMobile || isTablet || !hasArticles
                ? undefined
                : { borderTop: '1px solid var(--color-neutral-300)' }
            }
          >
            {!hasArticles && (
              <Paragraph color="secondary" size={isMobile || isTablet ? '100' : '200'}>
                <Trans id="requests.new.articles.no-article">No items added yet</Trans>
              </Paragraph>
            )}
            {showAddArticleButton && (
              <Button
                style={{ flex: isMobile || isTablet ? 1 : undefined }}
                variant="secondary"
                size="medium"
                onPress={handleAddItemClick}
                isLoading={
                  isPendingCreateArticle ||
                  isSuccessCreateArticle ||
                  isLoadingClientPurchaseHistory ||
                  isUpdatingExternalId
                }
                dataTrackingId="requests.new.articles.add"
              >
                <IconAdd />
                <Trans id="requests.new.articles.add">Add an item</Trans>
              </Button>
            )}
            {errors.articles?.hasError && (
              <Message type="error">
                {errors.articles?.noArticles ? (
                  workflow?.config?.fields?.request?.articles?.max === 1 ? (
                    <Trans id="requests.new.articles.error.no-article.single">
                      Please add an item
                    </Trans>
                  ) : (
                    <Trans id="requests.new.articles.error.no-article">
                      Please add at least one item
                    </Trans>
                  )
                ) : workflow?.config?.fields?.request?.articles?.max === 1 ? (
                  <Trans id="requests.new.articles.error.incomplete-article.single">
                    Please complete the item
                  </Trans>
                ) : (
                  <Trans id="requests.new.articles.error.incomplete-article">
                    Please complete all the items
                  </Trans>
                )}
              </Message>
            )}
          </Stack>
        )}
      </Box>

      {hasArticles && (
        <Stack row={isWidescreen} gap="1rem" alignItems={isWidescreen ? 'flex-start' : 'stretch'}>
          {workshopPrice.shown && <RequestWorkshopPrice />}
          {organizationPrice.shown && <RequestOrganizationPrice />}
        </Stack>
      )}

      {hasArticles && <ApplyHandlingFee />}

      <DepositSelector />
      <ClientAmountDueBox />

      {isClientManagerEnabled && clientPurchaseHistory && (
        <AddItemFromPurchaseHistoryModal
          isOpen={isPurchaseHistoryModalOpen}
          setIsOpen={setIsPurchaseHistoryModalOpen}
          onSelectProduct={handleCreateArticle}
          onAddNewItem={handleAddNewItem}
          purchaseHistoryData={clientPurchaseHistory}
        />
      )}

      {!isPurchaseHistoryModalOpen && serviceEligibility.enabled && (
        <ServiceEligibilityModal
          isOpen={isServiceEligibilityModalOpen}
          setIsOpen={setIsServiceEligibilityModalOpen}
        />
      )}

      {!isPurchaseHistoryModalOpen && !serviceEligibility.enabled && isOrganizationMultiBrands && (
        <BrandModal isOpen={isBrandModalOpen} setIsOpen={setIsBrandModalOpen} />
      )}
    </Stack>
  );
};
